import React from "react";
import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Card, CardBody, CardFooter, CardHeader, Col, Row, Form } from "react-bootstrap";
import { FormikProps, Formik, Field } from "formik";
import { Button } from "primereact/button";
import { request } from "../../../utils/Request";
import { find } from "lodash";
import { InputSwitchField } from "../../../components/Shared/InputSwitchField";
import { InputCheckboxField } from "../../../components/Shared/InputCheckboxField";
import { InputSelectField } from "../../../components/Shared/InputSelectField";
import { InputField } from "../../../components/Shared/InputField";

type TInputForm = {
    alp_auto_intransit_enabled: boolean;
    alp_auto_intransit_spot_load: boolean;
    alp_auto_intransit_inbound_load: boolean;
    alp_auto_intransit_outbound_load: boolean;
    alp_auto_intransit_outbound_group: boolean;

    alp_auto_intransit_spot_load_from_locations: string[];
    alp_auto_intransit_inbound_load_from_locations: string[];
    alp_auto_intransit_outbound_load_from_locations: string[];
    alp_auto_intransit_outbound_group_from_locations: string[];

    acm_supplier_identification_number: string | null;
};

const DEFAULT_FORM_STATE: TInputForm = {
    alp_auto_intransit_enabled: false,
    alp_auto_intransit_spot_load: false,
    alp_auto_intransit_inbound_load: false,
    alp_auto_intransit_outbound_load: false,
    alp_auto_intransit_outbound_group: false,

    alp_auto_intransit_spot_load_from_locations: [],
    alp_auto_intransit_inbound_load_from_locations: [],
    alp_auto_intransit_outbound_load_from_locations: [],
    alp_auto_intransit_outbound_group_from_locations: [],

    acm_supplier_identification_number: null,
};

interface IProps {
    t?: any;
    toastify?: any;
}

function GlobalSupplierSettingProfile(props: IProps) {
    const { t, toastify } = props;
    const [initFormState, setInitFormState] = useState<TInputForm>({ ...DEFAULT_FORM_STATE });
    const [locationOptions, setLocationOptions] = useState<any[]>([]);

    const getGlobalSettings = async () => {
        try {
            const response = await request({
                url: "/suppliers/global/settings",
            });
            const { data } = response.data;

            const v = (id: string, fallback: boolean = false) => {
                const item = find(data, { id });
                if ( item && typeof item.value !== "undefined" ) {
                    return Boolean(item.value);
                }
                return fallback;
            };

            const a = (id: string) => {
                const item = find(data, { id });
                return Array.isArray(item?.value) ? item.value : [];
            };

            const s = (id: string) => {
                const item = find(data, { id });
                const value = item?.value;
                return typeof value === "string" && value.trim() !== "" ? value : null;
            };

            setInitFormState({
                alp_auto_intransit_enabled: v("alp_auto_intransit_enabled", false),
                alp_auto_intransit_spot_load: v("alp_auto_intransit_spot_load", false),
                alp_auto_intransit_inbound_load: v("alp_auto_intransit_inbound_load", false),
                alp_auto_intransit_outbound_load: v("alp_auto_intransit_outbound_load", false),
                alp_auto_intransit_outbound_group: v("alp_auto_intransit_outbound_group", false),

                alp_auto_intransit_spot_load_from_locations: a("alp_auto_intransit_spot_load_from_locations"),
                alp_auto_intransit_inbound_load_from_locations: a("alp_auto_intransit_inbound_load_from_locations"),
                alp_auto_intransit_outbound_load_from_locations: a("alp_auto_intransit_outbound_load_from_locations"),
                alp_auto_intransit_outbound_group_from_locations: a("alp_auto_intransit_outbound_group_from_locations"),

                acm_supplier_identification_number: s("acm_supplier_identification_number"),
            });
        } catch (e) {
            // ignore and keep defaults
        }
    };

    const getLocations = async () => {
        try {
            const response = await request({
                url: "/suppliers/locations?per_page=-1",
            });
            const { data } = response.data;
            setLocationOptions(Array.isArray(data) ? data : []);
        } catch (e) {
            setLocationOptions([]);
        }
    };

    useEffect(() => {
        getGlobalSettings();
        getLocations();
    }, []);

    const onSubmit = async (payloads: TInputForm, { setSubmitting }: any) => {
        try {
            await request({
                method: "POST",
                url: "/suppliers/global/settings",
                data: { ...payloads },
            });

            toastify?.(t("record updated", { item: t("settings") }), "success");
            window.location.reload();
        } catch (e: any) {
            toastify?.(t("server error"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik enableReinitialize={ true } initialValues={ initFormState } onSubmit={ onSubmit }>
            { (form: FormikProps<TInputForm>) => {
                const disabled = !form.values.alp_auto_intransit_enabled || form.isSubmitting;

                return (
                    <Form onSubmit={ form.handleSubmit } className={ "mt-3" }>
                        <Card className={ "mb-3" }>
                            <CardHeader className={ "bg-body-tertiary" }>
                                <h4 className="card-title">{ t("global settings") }</h4>
                            </CardHeader>

                            <CardBody>
                                <Row>
                                    <Col sm={ 12 }>
                                        <Field
                                            component={ InputSwitchField }
                                            name="alp_auto_intransit_enabled"
                                            label={ t("Auto set loads to In Transit") }
                                            needBoolean
                                            disabled={ form.isSubmitting }
                                            onChange={ (nextValue: boolean) => {
                                                if ( nextValue ) {
                                                    form.setFieldValue("alp_auto_intransit_spot_load", true);
                                                    form.setFieldValue("alp_auto_intransit_inbound_load", true);
                                                    form.setFieldValue("alp_auto_intransit_outbound_load", true);
                                                    form.setFieldValue("alp_auto_intransit_outbound_group", true);
                                                }
                                            } }
                                        />
                                        <small className={ "text-muted d-block mt-1" }>
                                            { t("When enabled, ALPC will periodically move eligible loads to In Transit automatically.") }
                                        </small>
                                    </Col>
                                </Row>

                                <Row className={ "mt-3" }>
                                    <Col className="col-md-6 col-sm-12">
                                        <Field
                                            component={ InputCheckboxField }
                                            name="alp_auto_intransit_spot_load"
                                            label={ t("Spot Loads") }
                                            inline={ true }
                                            needBoolean
                                            disabled={ disabled }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-2">
                                        <Field
                                            component={ InputSelectField }
                                            name="alp_auto_intransit_spot_load_from_locations"
                                            label={ t("Limit by loading locations") }
                                            options={ locationOptions }
                                            disabled={
                                                disabled
                                                || !form.values.alp_auto_intransit_spot_load
                                            }
                                            placeholder={ t("All locations") }
                                            formText={ t("Leave empty to allow all locations.") }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-3">
                                        <Field
                                            component={ InputCheckboxField }
                                            name="alp_auto_intransit_inbound_load"
                                            label={ t("Inbound Loads") }
                                            inline={ true }
                                            needBoolean
                                            disabled={ disabled }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-3">
                                        <Field
                                            component={ InputSelectField }
                                            name="alp_auto_intransit_inbound_load_from_locations"
                                            label={ t("Limit by loading locations") }
                                            options={ locationOptions }
                                            disabled={
                                                disabled
                                                || !form.values.alp_auto_intransit_inbound_load
                                            }
                                            placeholder={ t("All locations") }
                                            formText={ t("Leave empty to allow all locations.") }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-3">
                                        <Field
                                            component={ InputCheckboxField }
                                            name="alp_auto_intransit_outbound_load"
                                            label={ t("Outbound Loads") }
                                            inline={ true }
                                            needBoolean
                                            disabled={ disabled }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-3">
                                        <Field
                                            component={ InputSelectField }
                                            name="alp_auto_intransit_outbound_load_from_locations"
                                            label={ t("Limit by loading locations") }
                                            options={ locationOptions }
                                            disabled={
                                                disabled
                                                || !form.values.alp_auto_intransit_outbound_load
                                            }
                                            placeholder={ t("All locations") }
                                            formText={ t("Leave empty to allow all locations.") }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-3">
                                        <Field
                                            component={ InputCheckboxField }
                                            name="alp_auto_intransit_outbound_group"
                                            label={ t("Outbound Load Groups (Aviso)") }
                                            inline={ true }
                                            needBoolean
                                            disabled={ disabled }
                                        />
                                    </Col>
                                    <Col className="col-md-6 col-sm-12 mt-3">
                                        <Field
                                            component={ InputSelectField }
                                            name="alp_auto_intransit_outbound_group_from_locations"
                                            label={ t("Limit by loading locations") }
                                            options={ locationOptions }
                                            disabled={
                                                disabled
                                                || !form.values.alp_auto_intransit_outbound_group
                                            }
                                            placeholder={ t("All locations") }
                                            formText={ t("Leave empty to allow all locations.") }
                                        />
                                    </Col>
                                </Row>
                            </CardBody>

                            <CardFooter>
                                <Button
                                    type="submit"
                                    size={ "small" }
                                    disabled={ form.isSubmitting }
                                    className="btn btn-success waves-effect btn-label waves-light mt-2"
                                >
                                    <i className="fas fa-save label-icon me-2" /> { t("let's save") }
                                </Button>
                            </CardFooter>
                        </Card>

                        <Card className={ "mb-3" }>
                            <CardHeader className={ "bg-body-tertiary" }>
                                <h4 className="card-title">{ t("acm connection") }</h4>
                            </CardHeader>

                            <CardBody>
                                <Row>
                                    <Col sm={ 12 } md={ 6 }>
                                        <Field
                                            component={ InputField }
                                            name="acm_supplier_identification_number"
                                            label={ t("acm supplier identification number") }
                                            placeholder={ t("enter acm supplier identification number") }
                                        />
                                        <small className={ "text-muted d-block mt-1" }>
                                            { t("acm connection help") }
                                        </small>
                                    </Col>
                                </Row>
                            </CardBody>

                            <CardFooter>
                                <Button
                                    type="submit"
                                    size={ "small" }
                                    disabled={ form.isSubmitting }
                                    className="btn btn-success waves-effect btn-label waves-light mt-2"
                                >
                                    <i className="fas fa-save label-icon me-2" /> { t("let's save") }
                                </Button>
                            </CardFooter>
                        </Card>
                    </Form>
                );
            } }
        </Formik>
    );
}

GlobalSupplierSettingProfile.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(GlobalSupplierSettingProfile);

